import { useMutation } from "convex/react";
import { useEffect, useRef, useState } from "react";

import { ModelSwitcher } from "@/components/models/ModelSwitcher";
import { BentoCard } from "@/components/ui/BentoCard";
import { useUser } from "@/hooks/useUser";
import { api } from "@convex/_generated/api";
import {
  clampEffort,
  DEFAULT_REASONING_EFFORT,
  DEFAULT_REASONING_MODEL_ID,
  modelById,
  REASONING_EFFORT_META,
} from "@domain/model-catalog";
import { cn } from "@/lib/utils";

import type { ReasoningEffort } from "@domain/model-catalog";
import type { Plan } from "@domain/claw-config";

export interface ReasoningSelection {
  modelId: string;
  effort: ReasoningEffort;
}

/** "GLM-5.2 · Standard" — the compact configuration summary used by the
 *  footer's active line and the dirty-state from→to preview. */
function summarize(sel: ReasoningSelection): string {
  const model = modelById(sel.modelId);
  const label =
    REASONING_EFFORT_META[clampEffort({ model, effort: sel.effort })].label;
  return `${model.name} · ${label}`;
}

interface AgentModelCardProps {
  /** The user's real billing plan — gates the frontier ("pro") models. */
  plan: Plan;
  /** The DRAFT selection shown in the picker (staged, not yet applied). */
  modelId: string;
  effort: ReasoningEffort;
  /** The APPLIED selection — powers the footer's active line and the
   *  dirty-state from→to preview. */
  saved: ReasoningSelection;
  onSelectModel: (modelId: string) => void;
  onSelectEffort: (effort: ReasoningEffort) => void;
  /** True when the draft differs from the applied config — enables the
   *  Revert/Update pair. */
  dirty: boolean;
  /** True while an Update is being applied — disables both buttons. */
  pending: boolean;
  onRevert: () => void;
  onUpdate: () => void;
  switchingNote?: string | null;
  /** Surfaced from the model gate's "See plans" CTA — the route scrolls the
   *  Subscription section into view. */
  onManagePlan?: () => void;
}

/**
 * Settings → Agent · Model — the presentational card (BentoCard + header +
 * ModelSwitcher + the always-present Revert/Update pair), fully controlled.
 * Selections STAGE into a draft; Update applies model + effort together in a
 * single mutation (⇒ a single pod restart), Revert discards the draft. Dev
 * previews mount THIS with local state; the real settings page mounts
 * AgentSection below, which owns the Convex wiring.
 */
export function AgentModelCard({
  plan,
  modelId,
  effort,
  saved,
  onSelectModel,
  onSelectEffort,
  dirty,
  pending,
  onRevert,
  onUpdate,
  switchingNote,
  onManagePlan,
}: AgentModelCardProps) {
  return (
    <BentoCard shadow className="p-8">
      <div className="mb-5">
        <div className="text-xs font-bold uppercase tracking-wider text-black/40 dark:text-white/40">
          Your agent's brain
        </div>
        <p className="mt-1 text-sm font-medium text-black/60 dark:text-white/60">
          One model runs everything your agent does — every chat, task, and
          workflow. Pick the brain and how deeply it thinks; changes apply
          together in a single restart.
        </p>
      </div>
      <ModelSwitcher
        plan={plan}
        modelId={modelId}
        effort={effort}
        onSelectModel={onSelectModel}
        onSelectEffort={onSelectEffort}
        switchingNote={switchingNote}
        onUpgrade={onManagePlan}
      />
      <div className="mt-6 flex items-center justify-between gap-4 border-t border-black/5 pt-4 dark:border-white/10">
        {dirty ? (
          <p className="min-w-0 text-[12px] font-medium leading-snug text-black/60 dark:text-white/60">
            <span className="text-black/40 line-through dark:text-white/40">
              {summarize(saved)}
            </span>
            <span className="mx-1.5 text-black/30 dark:text-white/30">→</span>
            <span className="font-bold text-black dark:text-white">
              {summarize({ modelId, effort })}
            </span>
            <span className="ml-1.5 text-black/40 dark:text-white/40">
              · one restart
            </span>
          </p>
        ) : (
          <p className="min-w-0 text-[12px] font-medium leading-snug text-black/45 dark:text-white/45">
            Active: <span className="font-bold">{summarize(saved)}</span>
          </p>
        )}
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onRevert}
            disabled={!dirty || pending}
            className={cn(
              "inline-flex min-h-[40px] items-center justify-center rounded-full px-4 font-body text-[13px] font-semibold transition-colors",
              "text-black/60 hover:text-black dark:text-white/60 dark:hover:text-white",
              "disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:text-black/60 dark:disabled:hover:text-white/60",
            )}
          >
            Revert
          </button>
          <button
            type="button"
            onClick={onUpdate}
            disabled={!dirty || pending}
            className={cn(
              "inline-flex min-h-[40px] items-center justify-center rounded-full bg-home-yellow px-5 font-body text-[13px] font-bold text-home-ink shadow-[0_10px_28px_-12px_rgba(33,31,28,0.4)] transition-transform",
              "hover:scale-[1.02] active:scale-[0.98]",
              "disabled:cursor-not-allowed disabled:opacity-35 disabled:shadow-none disabled:hover:scale-100 disabled:active:scale-100",
            )}
          >
            {pending ? "Updating…" : "Update"}
          </button>
        </div>
      </div>
    </BentoCard>
  );
}

interface AgentSectionProps {
  plan: Plan;
  onManagePlan?: () => void;
}

/** How long the re-braining toast stays up after the mutation settles. */
const SWITCHING_NOTE_MS = 3200;

/**
 * Settings → Agent · Model — the Convex container. Picker clicks STAGE into a
 * local draft (clamping the effort to the drafted model's supported set);
 * nothing is persisted or restarted until Update, which commits model + effort
 * in ONE `agentSettings.setReasoningConfig` call — so a model+effort change is
 * a single pod restart, never two. Revert drops the draft back to the applied
 * server state (`users.reasoningModelId`/`reasoningEffort` via `getMeSafe`).
 * The toast reports what actually happened (restarting vs applies-on-next-start
 * vs failed).
 */
export function AgentSection({ plan, onManagePlan }: AgentSectionProps) {
  const { user } = useUser();
  const setReasoningConfig = useMutation(api.agentSettings.setReasoningConfig);
  const [draft, setDraft] = useState<ReasoningSelection | null>(null);
  const [pending, setPending] = useState(false);
  const [switchingNote, setSwitchingNote] = useState<string | null>(null);
  const noteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (noteTimer.current) clearTimeout(noteTimer.current);
    },
    [],
  );

  const flashNote = (note: string) => {
    if (noteTimer.current) clearTimeout(noteTimer.current);
    setSwitchingNote(note);
    noteTimer.current = setTimeout(
      () => setSwitchingNote(null),
      SWITCHING_NOTE_MS,
    );
  };

  const saved: ReasoningSelection = {
    modelId: user?.reasoningModelId ?? DEFAULT_REASONING_MODEL_ID,
    effort: user?.reasoningEffort ?? DEFAULT_REASONING_EFFORT,
  };
  const current = draft ?? saved;
  const dirty =
    current.modelId !== saved.modelId || current.effort !== saved.effort;

  // A draft that has converged with the server (post-Update reactive echo, or
  // an identical re-selection) is dropped so the card keeps following server
  // state — e.g. a change applied from another tab.
  useEffect(() => {
    if (draft && !dirty) setDraft(null);
  }, [draft, dirty]);

  const update = () => {
    if (!dirty || pending) return;
    setPending(true);
    setReasoningConfig(current)
      .then(({ willRestart }) =>
        flashNote(
          willRestart
            ? "restarting your agent"
            : "saved — applies when your agent starts",
        ),
      )
      .catch(() => flashNote("update failed — try again"))
      .finally(() => setPending(false));
  };

  return (
    <AgentModelCard
      plan={plan}
      modelId={current.modelId}
      effort={current.effort}
      saved={saved}
      onSelectModel={(nextModelId) =>
        // Stage only — efforts are per-model, so clamp the drafted pair to
        // one the new model supports (the mutation rejects unsupported pairs).
        setDraft({
          modelId: nextModelId,
          effort: clampEffort({
            model: modelById(nextModelId),
            effort: current.effort,
          }),
        })
      }
      onSelectEffort={(nextEffort) =>
        setDraft({ modelId: current.modelId, effort: nextEffort })
      }
      dirty={dirty}
      pending={pending}
      onRevert={() => setDraft(null)}
      onUpdate={update}
      switchingNote={switchingNote}
      onManagePlan={onManagePlan}
    />
  );
}
